import { toast } from "react-toastify";
import ErrorToastBody from "../components/ErrorToastBody";

// Resolve the specific translated message for `data.error`/`data.error_message`
// when the backend provided one, falling back to `fallbackLabel` only if no
// specific translation is available (missing key => getLabel returns "#...").
const resolveErrorMessage = (data, tokenData, getLabel, fallbackLabel) => {
  if (!data?.error) return getLabel(fallbackLabel, tokenData);

  const interpTokens = { ...tokenData, ...(data?.details || {}) };
  let message;
  if (data?.details) {
    message = getLabel(data.error + "_detail", interpTokens);
    if (message.startsWith("#")) {
      message = getLabel(data.error, interpTokens);
    }
  } else {
    message = getLabel(data.error, tokenData);
  }

  if (message.startsWith("#")) {
    if (data?.error_message) return data.error_message;
    return getLabel(fallbackLabel, tokenData);
  }

  return message;
};

/**
 * Wrap Axios call with automatic toast handling
 * @param {Function} axiosCall - function returning axios promise
 * @param {string} toastId - toastId to use for loading/update
 * @param {object} tokenData - optional tokens for getLabel ({name, email, etc.})
 * @param {Function} getLabel - function to translate the labels
 * @returns {Promise<any>} - response data
 */
export const toastAxios = async (
  axiosCall,
  toastId,
  tokenData = {},
  getLabel
) => {
  // mostra il toast loading
  toast.loading(getLabel("toast_inProgress"), {
    toastId,
    type: toast.TYPE.INFO,
    position: toast.POSITION.BOTTOM_RIGHT,
    exclude: true,
  });

  try {
    const response = await axiosCall();

    // requires_confirmation: NON è un errore, è un flusso di conferma (HTTP 200).
    // Il backend non manda `success`, quindi senza questo check finirebbe nel
    // ramo error. Chiudiamo il toast di loading e lasciamo gestire al chiamante.
    if (response?.requires_confirmation === true) {
      toast.dismiss(toastId);
      return response;
    }

    // gestione success/error basata su server
    if (response?.success) {
      const successLabel = getLabel(response.success, tokenData);
      const render = successLabel.startsWith?.("#")
        ? (response.success_message || successLabel)
        : successLabel;
      toast.update(toastId, {
        render,
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 4000,
      });
    } else {
      // fallback error
      toast.update(toastId, {
        render: getLabel(response?.error || "toast_errorGeneric", tokenData),
        type: toast.TYPE.ERROR,
        isLoading: false,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 5000,
      });
    }

    return response;
  } catch (e) {
    console.error(e);
    const status = e?.response?.status;
    const data = e?.response?.data;
    // reqId per il debug: preferisci il body, fallback all'header X-Request-Id
    const reqId = data?.reqId || e?.response?.headers?.["x-request-id"];
    let message = "toast_errorGeneric"; // fallback

    if (status === 401) {
      message = resolveErrorMessage(data, tokenData, getLabel, "toast_unauthorized");
    } else if (status === 403) {
      if (data?.details?.resource) {
        const m = getLabel("permissions.denied_detail", { ...tokenData, resource: data.details.resource, action: data.details.action });
        message = m.startsWith("#")
          ? resolveErrorMessage(data, tokenData, getLabel, "toast_forbidden")
          : m;
      } else {
        message = resolveErrorMessage(data, tokenData, getLabel, "toast_forbidden");
      }
    }
    else if (status === 404) {
      message = resolveErrorMessage(data, tokenData, getLabel, "toast_notFound");
    }
    else if (status === 422 && data?.errors) {
      message = Object.values(data.errors)
        .flat()
        .map((err) => getLabel(err, tokenData))
        .join("\n");
    } else if (data?.error) {
      message = resolveErrorMessage(data, tokenData, getLabel, "toast_errorGeneric");
    }

    // testo di riferimento col reqId; fallback hardcoded se la label manca dal dizionario
    let refText = null;
    if (reqId) {
      refText = getLabel("toast_errorRef", { reqId });
      if (refText.startsWith("#")) refText = `Codice errore: ${reqId}`;
    }

    toast.update(toastId, {
      render: reqId ? (
        <ErrorToastBody message={message} refText={refText} reqId={reqId} />
      ) : (
        message
      ),
      type: toast.TYPE.ERROR,
      isLoading: false,
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: reqId ? 10000 : 5000,
    });

    // Optimistic-concurrency conflict (operation completion): surface a sentinel
    // (still shows the toast above) so the caller can refetch the stale entities
    // listed in details.conflicts and let the user review. Other callers ignore
    // it because it has no `success`.
    if (status === 409 && data?.error === "operations.conflict") {
      return { conflict: true, details: data?.details || null };
    }

    return null;
  }
};
